import { ExternalLink, Satellite, Shield } from "lucide-react";

const SPEC_IMAGE =
  "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/7mae578e_USE%20THIS%20IMAGE%20-%20MAIN%20VEHICLE.png";

const capabilities = [
  "4WD heavy-duty capability",
  "Remote-area reliability",
  "Dust, heat & rough-terrain ready",
  "Long-range fuel & water",
  "Designed for community empowerment",
];

const onboardGroups = [
  {
    title: "Horticulture",
    items: [
      "Tools & equipment",
      "Plant nursery & pots",
      "Portable nursery setup",
      "Collapsible work area",
    ],
  },
  {
    title: "Water & Power",
    items: [
      "Water storage 500–1000L",
      "Piping, fittings & irrigation",
      "Solar power system",
      "Generator & power independence",
    ],
  },
  {
    title: "Shelter & Storage",
    items: [
      "Retractable shade awning",
      "Roof storage, tent & gear",
      "Secure lockable storage",
      "Sleeping space for 1–2 people",
    ],
  },
  {
    title: "Connectivity",
    items: [
      "Starlink satellite link",
      "Satellite comms",
      "Remote connectivity",
    ],
  },
];

export default function MobileUnit() {
  return (
    <section
      id="mobile-unit"
      data-testid="mobile-unit-section"
      className="relative bg-stone-alt py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-rust mb-6">
              The Mobile Unit
            </div>
            <h2
              data-testid="mobile-unit-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-charcoal"
            >
              Growing Country
              <span className="block italic text-rust">
                Remote Horticulture Support Unit.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-ink">
              A purpose-built 4WD horticulture support unit that delivers
              everything needed to establish, maintain and sustain community
              gardens in remote Aboriginal and Torres Strait Islander
              communities — and stays connected wherever Country takes it.
            </p>
          </div>
        </div>

        {/* Full-width infographic */}
        <figure className="relative bg-sand border border-stone-line">
          <a
            href={SPEC_IMAGE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-unit-zoom-link"
            className="block group relative"
            aria-label="Open full-resolution spec sheet"
          >
            <img
              src={SPEC_IMAGE}
              alt="Growing Country Remote Horticulture Support Unit — annotated spec sheet showing tools, plant nursery, water & irrigation, lockable storage, generator, solar, roof tent and sleeping quarters"
              className="w-full h-auto block"
            />
            <div className="absolute top-4 right-4 inline-flex items-center gap-2 bg-charcoal text-sand px-4 py-2 text-[10px] uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity">
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              Open full size
            </div>
          </a>
          <figcaption className="px-6 md:px-8 py-4 border-t border-stone-line text-[11px] uppercase tracking-[0.25em] text-ink/70 flex items-center justify-between gap-4">
            <span>Practical infrastructure for remote food security</span>
            <a
              href={SPEC_IMAGE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-unit-zoom-text"
              className="inline-flex items-center gap-2 text-rust hover:text-rust-dark transition-colors"
            >
              View full resolution
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
            </a>
          </figcaption>
        </figure>

        {/* Capability + Onboard */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-rust mb-5">
              Built for Country
            </div>
            <ul className="space-y-4">
              {capabilities.map((c, i) => (
                <li
                  key={c}
                  data-testid={`capability-${i}`}
                  className="flex items-start gap-4 border-b border-stone-line/70 pb-4"
                >
                  <span className="font-serif text-rust text-xl tick-num leading-none w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg md:text-xl text-charcoal leading-snug pt-0.5">
                    {c}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink">
              Delivering the tools, training support and resources to empower
              communities to grow fresh food, build skills and strengthen food
              security on Country.
            </p>
          </div>

          <div className="md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-sage mb-5">
              Everything on board
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-stone-line border border-stone-line">
              {onboardGroups.map((g, gi) => (
                <div
                  key={g.title}
                  data-testid={`onboard-group-${gi}`}
                  className="bg-sand p-6"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-rust mb-4">
                    {g.title === "Connectivity" && (
                      <Satellite
                        className="w-3.5 h-3.5"
                        strokeWidth={1.5}
                      />
                    )}
                    {g.title}
                  </div>
                  <ul className="space-y-2.5">
                    {g.items.map((item) => (
                      <li
                        key={item}
                        className="text-sm md:text-base text-charcoal flex items-start gap-3 leading-snug"
                      >
                        <span
                          className="w-1.5 h-1.5 bg-sage shrink-0 mt-2"
                          aria-hidden
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compatibility callout */}
        <div
          data-testid="mobile-unit-compatibility"
          className="mt-12 md:mt-16 bg-charcoal text-sand p-6 md:p-8 flex items-start gap-5 relative overflow-hidden"
        >
          <div className="absolute inset-0 grain-overlay opacity-30 pointer-events-none" />
          <Shield
            className="w-6 h-6 text-ochre shrink-0 mt-1 relative"
            strokeWidth={1.5}
          />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-ochre/85 mb-2">
              Compatibility
            </div>
            <div className="font-serif text-2xl md:text-3xl font-light leading-snug">
              Compatible with Indigenous Ranger and Community Programs.
            </div>
            <p className="mt-3 text-sm md:text-base text-sand/75 leading-relaxed max-w-3xl">
              The unit integrates with existing Ranger operations, school
              programs and community organisations — designed to complement
              rather than replace the systems already in place on Country.
            </p>
          </div>
        </div>

        {/* Tagline */}
        <div className="mt-16 md:mt-20 pt-10 border-t border-stone-line/70 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="font-serif text-2xl md:text-3xl italic text-charcoal">
            Deliver. Empower. Grow.
            <span className="block not-italic text-rust">
              Practical infrastructure for remote food security.
            </span>
          </div>
          <a
            href="/#contact"
            data-testid="mobile-unit-cta"
            className="inline-flex items-center gap-3 bg-rust text-sand px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-rust-dark transition-colors self-start md:self-auto"
          >
            Partner on a unit
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
